import { motion } from "framer-motion";
import { ArrowRight, FileCheck2, Stamp, BadgeCheck, ChevronDown, Building2 } from "lucide-react";

const RING_1 = ["REGISTRATIONS", "LICENSES", "GST", "TAX", "MSME", "IP", "FOOD"];
const RING_2 = ["IMPORT/EXPORT", "QUALITY", "INDUSTRY APPROVALS", "CERTIFICATIONS"];

const FLOATING_ICONS = [
  { Icon: FileCheck2, color: "#00B0FF", style: { top: "18%", left: "12%" }, delay: "0s" },
  { Icon: Stamp, color: "#FF8800", style: { top: "24%", right: "10%" }, delay: "1.2s" },
  { Icon: BadgeCheck, color: "#00E676", style: { bottom: "22%", right: "16%" }, delay: "2s" },
  { Icon: FileCheck2, color: "#FF8800", style: { bottom: "18%", left: "16%" }, delay: "0.6s" },
];

export function RegHero() {
  return (
    <section data-testid="reg-hero" className="relative min-h-[92vh] flex items-center overflow-hidden bg-grid-tech">
      <div className="absolute inset-0 bg-[radial-gradient(ellipse_at_center,rgba(0,176,255,0.07),transparent_60%)]" />
      <div className="absolute inset-0 bg-[radial-gradient(ellipse_at_bottom_right,rgba(255,136,0,0.06),transparent_55%)]" />

      {FLOATING_ICONS.map(({ Icon, color, style, delay }, i) => (
        <div
          key={i}
          className="absolute hidden lg:flex w-12 h-12 rounded-xl glass items-center justify-center animate-float pointer-events-none"
          style={{ ...style, animationDelay: delay, boxShadow: `0 0 25px ${color}22` }}
        >
          <Icon className="w-5 h-5" style={{ color }} strokeWidth={1.8} />
        </div>
      ))}

      {/* Orbiting compliance ecosystem */}
      <div className="absolute inset-0 pointer-events-none hidden lg:flex items-center justify-center">
        <div className="relative w-[860px] h-[860px]">
          <div className="absolute left-1/2 top-1/2 -translate-x-1/2 -translate-y-1/2">
            <div className="w-40 h-40 rounded-full glass flex flex-col items-center justify-center text-center shadow-[0_0_70px_rgba(0,176,255,0.22)]">
              <Building2 className="w-8 h-8 text-[#00B0FF] mb-2" strokeWidth={1.8} />
              <p className="font-display font-extrabold tracking-[0.12em] text-white text-sm leading-tight px-3">
                YOUR<br />BUSINESS
              </p>
            </div>
          </div>
          <div className="orbit-ring absolute inset-0 rounded-full border border-white/5">
            {RING_1.map((n, i) => (
              <div key={n} className="absolute inset-0" style={{ transform: `rotate(${(360 / RING_1.length) * i}deg)` }}>
                <div className="absolute -top-3.5 left-1/2 -translate-x-1/2">
                  <div className="orbit-chip">
                    <span
                      className="glass inline-block px-3 py-1.5 rounded-full font-mono-tech text-[10px] tracking-[0.18em] text-slate-200 whitespace-nowrap"
                      style={{ transform: `rotate(${-(360 / RING_1.length) * i}deg)` }}
                    >
                      {n}
                    </span>
                  </div>
                </div>
              </div>
            ))}
          </div>
          <div className="orbit-ring-2 absolute inset-[130px] rounded-full border border-dashed border-[#FF8800]/15">
            {RING_2.map((n, i) => (
              <div key={n} className="absolute inset-0" style={{ transform: `rotate(${(360 / RING_2.length) * i}deg)` }}>
                <div className="absolute -top-3 left-1/2 -translate-x-1/2">
                  <div className="orbit-chip-2">
                    <span
                      className="inline-block px-2.5 py-1 rounded-full bg-[#FF8800]/10 border border-[#FF8800]/25 font-mono-tech text-[9px] tracking-[0.16em] text-[#FF8800] whitespace-nowrap"
                      style={{ transform: `rotate(${-(360 / RING_2.length) * i}deg)` }}
                    >
                      {n}
                    </span>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>

      <div className="relative z-10 max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 pt-32 pb-24 w-full">
        <div className="max-w-2xl">
          <motion.p
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.7 }}
            className="font-mono-tech text-xs sm:text-sm tracking-[0.4em] text-[#00B0FF] uppercase mb-6"
          >
            LaunchLab — Registrations & Certifications
          </motion.p>
          <motion.h1
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.85, delay: 0.15 }}
            className="font-display text-4xl sm:text-6xl lg:text-7xl font-extrabold tracking-tight leading-[1.04]"
            data-testid="reg-hero-headline"
          >
            <span className="text-gradient-hero">GET YOUR BUSINESS</span>
            <br />
            <span className="text-gradient-saffron">REGISTERED.</span>{" "}
            <span className="text-gradient-hero">GET READY</span>
            <br />
            <span className="text-gradient-hero">TO </span>
            <span className="text-gradient-india">GROW.</span>
          </motion.h1>
          <motion.p
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.85, delay: 0.3 }}
            className="mt-6 text-base sm:text-lg text-slate-300 leading-relaxed max-w-xl"
            data-testid="reg-hero-subtext"
          >
            LaunchLab helps entrepreneurs identify, prepare and coordinate the registrations,
            licenses and certifications their business may require.
          </motion.p>
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.85, delay: 0.45 }}
            className="mt-10"
          >
            <a
              href="#smart-check"
              data-testid="reg-hero-cta"
              className="group inline-flex items-center gap-2 px-8 py-4 rounded-full bg-gradient-to-r from-[#FF8800] to-[#ff5e00] text-[#06080E] font-semibold text-sm tracking-wide transition-transform duration-300 hover:scale-105 shadow-[0_0_40px_rgba(255,136,0,0.35)]"
            >
              START YOUR BUSINESS JOURNEY
              <ArrowRight className="w-4 h-4 transition-transform duration-300 group-hover:translate-x-1" />
            </a>
          </motion.div>
        </div>
      </div>

      <a
        href="#reg-categories"
        data-testid="reg-scroll-down"
        aria-label="Scroll to what LaunchLab helps with"
        className="absolute bottom-7 left-1/2 -translate-x-1/2 z-10 flex flex-col items-center gap-1.5 text-slate-500 hover:text-[#FF8800] transition-colors"
      >
        <span className="font-mono-tech text-[9px] tracking-[0.35em] uppercase">Scroll</span>
        <ChevronDown className="w-5 h-5 animate-bounce" />
      </a>

      <div className="absolute bottom-0 left-0 right-0 h-40 bg-gradient-to-b from-transparent to-[#06080E] pointer-events-none" />
    </section>
  );
}
